import { useState } from 'react';
import PropTypes from 'prop-types';

const REGISTRARS = [
  { value: 'godaddy', label: 'GoDaddy' },
  { value: 'namecheap', label: 'Namecheap' },
  { value: 'cloudflare', label: 'Cloudflare' },
  { value: 'other', label: 'Other' },
];

const EMAIL_SERVICES = [
  { value: 'google', label: 'Google Workspace' },
  { value: 'microsoft', label: 'Microsoft 365' },
  { value: 'other', label: 'Other' },
];

const DKIM_INSTRUCTIONS = {
  godaddy: [
    'Log in to your GoDaddy account and open the DNS Management page for your domain.',
    'Click "Add" to create a new DNS record.',
    'Select "TXT" as the record type.',
    'Enter the DKIM Host value in the "Host" field (omit your domain name — GoDaddy appends it automatically).',
    'Paste the full DKIM Value into the "TXT Value" field.',
    'Set TTL to 1 hour (3600 seconds) and save.',
  ],
  namecheap: [
    'Log in to Namecheap and go to "Domain List" > select your domain > "Advanced DNS".',
    'Click "Add New Record" and select "TXT Record".',
    'Enter the DKIM Host in the "Host" field (use the part before your domain, e.g. pm._domainkey).',
    'Paste the DKIM Value into the "Value" field.',
    'Set TTL to "Automatic" and save.',
  ],
  cloudflare: [
    'Log in to Cloudflare and select your domain.',
    'Go to "DNS" > "Records" and click "Add record".',
    'Select "TXT" as the type.',
    'Enter the full DKIM Host (including your domain) in the "Name" field.',
    'Paste the DKIM Value into the "Content" field.',
    'Ensure "Proxy status" is set to DNS Only (grey cloud) and save.',
  ],
  other: [
    "Log in to your domain registrar's control panel.",
    'Navigate to the DNS management section for your domain.',
    'Create a new TXT record.',
    'Set the Host/Name to the DKIM Host value shown above.',
    'Set the Value/Content to the DKIM Value shown above.',
    'Save the record and allow time for propagation.',
  ],
};

const RETURN_PATH_INSTRUCTIONS = {
  godaddy: [
    'In GoDaddy DNS Management, click "Add" and select "CNAME".',
    'Set the "Host" to the Return-Path Host value (e.g. pm-bounces).',
    'Set the "Points to" value to pm.mtasv.net.',
    'Save the record.',
  ],
  namecheap: [
    'In Namecheap Advanced DNS, click "Add New Record" and select "CNAME Record".',
    'Enter the Return-Path Host in the "Host" field (e.g. pm-bounces).',
    'Set the "Value" to pm.mtasv.net.',
    'Save the record.',
  ],
  cloudflare: [
    'In Cloudflare DNS, click "Add record" and select "CNAME".',
    'Enter the Return-Path Host in the "Name" field (e.g. pm-bounces.yourdomain.com).',
    'Set the "Target" to pm.mtasv.net.',
    'Ensure "Proxy status" is DNS Only and save.',
  ],
  other: [
    'In your DNS management panel, add a new CNAME record.',
    'Set the Host/Name to the Return-Path Host shown above.',
    'Set the Value/Target to pm.mtasv.net.',
    'Save the record.',
  ],
};

const EMAIL_SERVICE_DKIM_NOTES = {
  google:
    'If you use Google Workspace, ensure you have not previously configured a DKIM record for this domain in Google Admin — two DKIM records for the same selector can cause conflicts.',
  microsoft:
    'If you use Microsoft 365, ensure you have not already enabled DKIM for this domain in the Microsoft 365 admin center, as conflicting records may affect deliverability.',
  other: null,
};

/**
 * Expandable help section with tabbed DKIM / Return-Path instructions.
 * Instructions vary by registrar selection; email service adds a DKIM-only note.
 */
const DnsInstructions = ({ dkimHost, returnPathHost }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [activeTab, setActiveTab] = useState('dkim');
  const [registrar, setRegistrar] = useState('godaddy');
  const [emailService, setEmailService] = useState('other');

  const dkimSteps = DKIM_INSTRUCTIONS[registrar] || DKIM_INSTRUCTIONS.other;
  const returnPathSteps = RETURN_PATH_INSTRUCTIONS[registrar] || RETURN_PATH_INSTRUCTIONS.other;
  const emailServiceNote = EMAIL_SERVICE_DKIM_NOTES[emailService];

  return (
    <div className="dns-instructions">
      <button type="button" className="dns-instructions--toggle" onClick={() => setIsExpanded((v) => !v)}>
        Need help setting up your DNS records?
        <span className="dns-instructions--toggle-icon">{isExpanded ? '▲' : '▼'}</span>
      </button>

      {isExpanded && (
        <div className="dns-instructions--content">
          <div className="dns-instructions--tabs">
            <button
              type="button"
              className={`dns-instructions--tab ${activeTab === 'dkim' ? 'is-active' : ''}`}
              onClick={() => setActiveTab('dkim')}
            >
              DKIM
            </button>
            <button
              type="button"
              className={`dns-instructions--tab ${activeTab === 'return_path' ? 'is-active' : ''}`}
              onClick={() => setActiveTab('return_path')}
            >
              Return-Path
            </button>
          </div>

          <div className="dns-instructions--selectors">
            <label htmlFor="registrar-select" className="dns-instructions--selector-label">
              Domain Registrar
            </label>
            <select
              id="registrar-select"
              className="dns-instructions--select"
              value={registrar}
              onChange={(e) => setRegistrar(e.target.value)}
            >
              {REGISTRARS.map((r) => (
                <option key={r.value} value={r.value}>
                  {r.label}
                </option>
              ))}
            </select>

            {activeTab === 'dkim' && (
              <>
                <label htmlFor="email-service-select" className="dns-instructions--selector-label">
                  Email Service
                </label>
                <select
                  id="email-service-select"
                  className="dns-instructions--select"
                  value={emailService}
                  onChange={(e) => setEmailService(e.target.value)}
                >
                  {EMAIL_SERVICES.map((s) => (
                    <option key={s.value} value={s.value}>
                      {s.label}
                    </option>
                  ))}
                </select>
              </>
            )}
          </div>

          {activeTab === 'dkim' && (
            <div className="dns-instructions--steps">
              <p className="dns-instructions--record-ref">
                Adding TXT record for: <code>{dkimHost}</code>
              </p>
              {emailServiceNote && <div className="dns-instructions--service-note">{emailServiceNote}</div>}
              <ol>
                {dkimSteps.map((step, i) => (
                  <li key={i}>{step}</li>
                ))}
              </ol>
            </div>
          )}

          {activeTab === 'return_path' && (
            <div className="dns-instructions--steps">
              <p className="dns-instructions--record-ref">
                Adding CNAME record for: <code>{returnPathHost}</code>
              </p>
              <ol>
                {returnPathSteps.map((step, i) => (
                  <li key={i}>{step}</li>
                ))}
              </ol>
            </div>
          )}

          <p className="dns-instructions--propagation-note">
            <strong>Note:</strong> DNS changes can take up to 48 hours to propagate, though most changes are visible
            within a few minutes to a few hours.
          </p>
        </div>
      )}
    </div>
  );
};

DnsInstructions.propTypes = {
  dkimHost: PropTypes.string.isRequired,
  returnPathHost: PropTypes.string.isRequired,
};

export default DnsInstructions;
